import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Bolt as BoltIcon,
    Close as CloseIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    History as HistoryIcon,
    Menu as MenuIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Send as SendIcon,
    SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import {
    Avatar,
    alpha,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { EChatAgentMessageAuthor, IChatAgentMessage } from '@sharedInterfaces/ChatAgent';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { renderFormattedText } from '../../_UnderConstruction/MvpChatAgent-2/components/TextFormatter';
import { agentActions, chatThreads, shortcuts } from '../../_UnderConstruction/MvpChatAgent-2/components/types';
import { useChatAgent } from '../../Agents/hooks/useChatAgent';

export interface MvpAgentChatDialogProps {
    open: boolean;
    onClose: () => void;
    initialMessage?: string;
}

export function MvpAgentChatDialog({ open, onClose, initialMessage = '' }: MvpAgentChatDialogProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).chatAgent;
    const employee = useSelector((state: AppState) => state.employee);

    // Chat state
    const {
        chat,
        messages,
        isTyping,
        sendMessage: sendChatMessage,
        resetChat,
        getChatList,
        loadChat,
    } = useChatAgent({
        dummyMessage: langStrings.howCanIHelp,
    });
    const chatList = useSelector((state: AppState) => state.chatAgent.chatList);
    const [input, setInput] = useState(initialMessage);

    // UI state
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [chatHistoryOpen, setChatHistoryOpen] = useState(true);
    const [chatSearchOpen, setChatSearchOpen] = useState(false);
    const [agentActionOpen, setAgentActionOpen] = useState(false);
    const [chatSearchTerm, setChatSearchTerm] = useState('');

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Feature flags
    const ENABLE_AGENT_ACTION_PANEL = false;

    // Close sidebar on mobile when dialog opens
    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false);
        } else {
            setSidebarOpen(true);
        }
    }, [isMobile, open]);

    // Load chat list when dialog opens and history is expanded by default
    useEffect(() => {
        if (open && chatHistoryOpen) {
            getChatList();
        }
    }, [open]);

    const handleNewChat = () => {
        resetChat();
        setInput('');
        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleSendMessage = async () => {
        const trimmedInput = input.trim();
        if (!trimmedInput) return;

        const success = await sendChatMessage(trimmedInput);
        if (success) {
            setInput('');
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    const handleChatHistoryClick = (chatId: string) => {
        loadChat(chatId);
        if (isMobile) {
            setSidebarOpen(false);
        }
    };

    // Scroll to bottom when messages change
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [messages]);

    // Sidebar content component
    const SidebarContent = () => (
        <>
            {/* Sidebar Header */}
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <IconButton size="small" sx={{ color: theme.palette.text.secondary }} onClick={toggleSidebar}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 400, color: theme.palette.text.primary }}>
                        {langStrings.myChatTitle}
                    </Typography>
                </Box>
            </Box>

            {/* Collapsible Sections */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {/* 1. Chat History Section */}
                <Paper sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.7), m: 2, mb: 1 }}>
                    <ListItemButton
                        onClick={() => {
                            setChatHistoryOpen(!chatHistoryOpen);
                            if (!chatHistoryOpen) {
                                getChatList();
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 36 }}>
                            <HistoryIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </Box>
                        <ListItemText
                            primary={langStrings.chatHistory}
                            primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                getChatList();
                            }}
                            sx={{ mr: 1 }}
                        >
                            <RefreshIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </IconButton>
                        {chatHistoryOpen ? (
                            <ExpandLessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        ) : (
                            <ExpandMoreIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        )}
                    </ListItemButton>
                    <Collapse in={chatHistoryOpen}>
                        <Box sx={{ p: 2, pt: 1 }}>
                            <Button
                                fullWidth
                                startIcon={<AddIcon />}
                                onClick={handleNewChat}
                                sx={{
                                    justifyContent: 'flex-start',
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                                    mb: 2,
                                    borderRadius: 2,
                                }}
                            >
                                {langStrings.newChat}
                            </Button>

                            <Typography
                                variant="caption"
                                sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}
                            >
                                {langStrings.lastChats}
                            </Typography>

                            <List sx={{ p: 0 }}>
                                {chatList.length > 0 ? (
                                    chatList.slice(0, 4).map((chatItem, index) => (
                                        <ListItem key={chatItem.chatId} disablePadding sx={{ mb: 0.5 }}>
                                            <ListItemButton
                                                onClick={() => handleChatHistoryClick(chatItem.chatId)}
                                                sx={{
                                                    borderRadius: 1,
                                                    backgroundColor:
                                                        chat?.chatId === chatItem.chatId
                                                            ? alpha(theme.palette.primary.main, 0.2)
                                                            : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                    },
                                                    py: 1,
                                                }}
                                            >
                                                <ListItemText
                                                    primary={chatItem.title}
                                                    secondary={`${new Date(chatItem.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${new Date(chatItem.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        sx: {
                                                            fontSize: '0.75rem',
                                                            fontWeight: chat?.chatId === chatItem.chatId ? 600 : 400,
                                                            color:
                                                                chat?.chatId === chatItem.chatId
                                                                    ? theme.palette.primary.main
                                                                    : theme.palette.text.primary,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        },
                                                    }}
                                                    secondaryTypographyProps={{
                                                        variant: 'caption',
                                                        sx: {
                                                            fontSize: '0.65rem',
                                                            color: theme.palette.text.secondary,
                                                        },
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>
                                        <ListItemText
                                            primary="Keine Chats vorhanden"
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: {
                                                    fontSize: '0.75rem',
                                                    color: theme.palette.text.secondary,
                                                    fontStyle: 'italic',
                                                },
                                            }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    </Collapse>
                </Paper>

                {/* 2. Chat Search Section */}
                <Paper sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.7), m: 2, mb: 1 }}>
                    <ListItemButton onClick={() => setChatSearchOpen(!chatSearchOpen)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 36 }}>
                            <SearchIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        </Box>
                        <ListItemText
                            primary={langStrings.chatSearch}
                            primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                            }}
                        />
                        {chatSearchOpen ? (
                            <ExpandLessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        ) : (
                            <ExpandMoreIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                        )}
                    </ListItemButton>
                    <Collapse in={chatSearchOpen}>
                        <Box sx={{ p: 2, pt: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={langStrings.searchPlaceholder}
                                value={chatSearchTerm}
                                onChange={(e) => setChatSearchTerm(e.target.value)}
                                sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Collapse>
                </Paper>

                {/* 3. Agent in Action Section - Feature Flag */}
                {ENABLE_AGENT_ACTION_PANEL && (
                    <Paper sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.7), m: 2, mb: 1 }}>
                        <ListItemButton onClick={() => setAgentActionOpen(!agentActionOpen)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 36 }}>
                                <BoltIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                            </Box>
                            <ListItemText
                                primary="Agent in Action"
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                }}
                            />
                            {agentActionOpen ? (
                                <ExpandLessIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                            ) : (
                                <ExpandMoreIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                            )}
                        </ListItemButton>
                        <Collapse in={agentActionOpen}>
                            <Box sx={{ p: 2, pt: 1 }}>
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                    Agent-Aktionen werden hier angezeigt
                                </Typography>
                            </Box>
                        </Collapse>
                    </Paper>
                )}
            </Box>
        </>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    width: isMobile ? '100vw' : '95vw',
                    height: isMobile ? '100vh' : '90vh',
                    maxWidth: isMobile ? 'none' : '1400px',
                    maxHeight: isMobile ? 'none' : '800px',
                    borderRadius: isMobile ? 0 : 3,
                    overflow: 'hidden',
                    m: isMobile ? 0 : 'auto',
                },
            }}
        >
            <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: isMobile ? 2 : 3,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        minHeight: isMobile ? 56 : 64,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                fontSize: isMobile ? '1.25rem' : '1.5rem',
                            }}
                        >
                            <SmartToyIcon sx={{ color: theme.palette.secondary.main, fontSize: isMobile ? 20 : 24 }} />
                            <Typography
                                variant={isMobile ? 'h6' : 'h3'}
                                sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                T4LO
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {/* Sidebar - Desktop */}
                    {!isMobile && (
                        <Box
                            sx={{
                                width: sidebarOpen ? 320 : 0,
                                backgroundColor: alpha(theme.palette.background.default, 0.5),
                                borderRight: sidebarOpen ? `1px solid ${theme.palette.divider}` : 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'width 0.3s ease',
                                overflow: 'hidden',
                            }}
                        >
                            {sidebarOpen && <SidebarContent />}
                        </Box>
                    )}

                    {/* Sidebar - Mobile Drawer */}
                    {isMobile && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1300,
                                display: sidebarOpen ? 'flex' : 'none',
                            }}
                        >
                            {/* Backdrop */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    zIndex: 1,
                                }}
                                onClick={() => setSidebarOpen(false)}
                            />

                            {/* Sidebar Content */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    width: 280,
                                    backgroundColor: alpha(theme.palette.background.default, 0.9),
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                                    transition: 'transform 0.3s ease-in-out',
                                }}
                            >
                                <SidebarContent />
                            </Box>
                        </Box>
                    )}

                    {/* Main Chat Area */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Chat Header */}
                        <Box
                            sx={{
                                p: isMobile ? 2 : 3,
                                /*  borderBottom: `1px solid ${theme.palette.divider}`, */
                                backgroundColor: alpha(theme.palette.background.default, 0.3),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                minHeight: isMobile ? 56 : 64,
                            }}
                        >
                            <IconButton
                                onClick={toggleSidebar}
                                sx={{
                                    color: theme.palette.text.secondary,
                                    mr: 2,
                                    display: isMobile || !sidebarOpen ? 'flex' : 'none',
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Typography
                                variant={isMobile ? 'subtitle1' : 'h4'}
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 400,
                                    flex: 1,
                                    textAlign: 'center',
                                    fontSize: isMobile ? '1rem' : '1.25rem',
                                }}
                            >
                                {employee?.firstName
                                    ? langStrings.howCanIHelpWithName.replace('{firstName}', employee.firstName)
                                    : langStrings.howCanIHelp}
                            </Typography>
                            <Tooltip title={langStrings.newChat}>
                                <IconButton
                                    onClick={handleNewChat}
                                    aria-label={langStrings.newChat}
                                    sx={{
                                        color: theme.palette.primary.main,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                                        ml: 2,
                                    }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Messages Area */}
                        <Box
                            ref={scrollRef}
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                p: isMobile ? 2 : 3,
                                backgroundColor: alpha(theme.palette.background.default, 0.3),
                                display: 'flex',
                                flexDirection: 'column',
                                gap: isMobile ? 2 : 3,
                            }}
                        >
                            {messages.length === 0 ? (
                                <Box sx={{ textAlign: 'center', p: 4 }}>
                                    <Typography color="textSecondary">
                                        {langStrings.chatEmptyText || 'Stellen Sie eine Frage, um zu beginnen.'}
                                    </Typography>
                                </Box>
                            ) : (
                                messages.map((message, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            display: 'flex',
                                            justifyContent:
                                                message.role === EChatAgentMessageAuthor.USER
                                                    ? 'flex-end'
                                                    : 'flex-start',
                                        }}
                                    >
                                        {message.role === EChatAgentMessageAuthor.AI && (
                                            <Avatar
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    width: isMobile ? 28 : 32,
                                                    height: isMobile ? 28 : 32,
                                                    mr: isMobile ? 1 : 2,
                                                    mt: 0.5,
                                                }}
                                            >
                                                <SmartToyIcon fontSize="small" />
                                            </Avatar>
                                        )}
                                        <Paper
                                            sx={{
                                                p: isMobile ? 2 : 2.5,
                                                maxWidth: isMobile ? '85%' : '70%',
                                                backgroundColor:
                                                    message.role === EChatAgentMessageAuthor.USER
                                                        ? theme.palette.primary.main
                                                        : theme.palette.background.paper,
                                                color:
                                                    message.role === EChatAgentMessageAuthor.USER
                                                        ? theme.palette.primary.contrastText
                                                        : theme.palette.text.primary,
                                                borderRadius:
                                                    message.role === EChatAgentMessageAuthor.USER
                                                        ? '20px 20px 4px 20px'
                                                        : '20px 20px 20px 4px',
                                                boxShadow: theme.shadows[2],
                                            }}
                                        >
                                            {renderFormattedText(
                                                message.text,
                                                message.role === EChatAgentMessageAuthor.USER,
                                            )}
                                        </Paper>
                                    </Box>
                                ))
                            )}

                            {isTyping && (
                                <Box sx={{ display: 'flex' }}>
                                    <Avatar
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            width: isMobile ? 28 : 32,
                                            height: isMobile ? 28 : 32,
                                            mr: isMobile ? 1 : 2,
                                            mt: 0.5,
                                        }}
                                    >
                                        <SmartToyIcon fontSize="small" />
                                    </Avatar>
                                    <Paper
                                        sx={{
                                            p: isMobile ? 2 : 2.5,
                                            backgroundColor: theme.palette.background.paper,
                                            borderRadius: '20px 20px 20px 4px',
                                            boxShadow: theme.shadows[2],
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{ fontSize: isMobile ? '0.875rem' : '0.95rem' }}
                                        >
                                            Schreibt...
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </Box>

                        {/* Input Area */}
                        <Box
                            sx={{
                                p: isMobile ? 2 : 3,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                backgroundColor: theme.palette.background.paper,
                                paddingBottom: isMobile ? 'max(16px, env(safe-area-inset-bottom))' : 3,
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, alignItems: 'flex-end' }}>
                                <TextField
                                    inputRef={inputRef}
                                    fullWidth
                                    multiline
                                    maxRows={isMobile ? 3 : 4}
                                    placeholder={langStrings.inputPlaceholder || 'Was kann ich heute für dich tun?'}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={false}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: isMobile ? 2 : 3,
                                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                                            fontSize: isMobile ? '0.875rem' : '1rem',
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleSendMessage}
                                                    disabled={!input.trim()}
                                                    sx={{
                                                        backgroundColor: input.trim()
                                                            ? theme.palette.primary.main
                                                            : theme.palette.action.disabled,
                                                        color: theme.palette.primary.contrastText,
                                                        width: isMobile ? 36 : 40,
                                                        height: isMobile ? 36 : 40,
                                                        '&:hover': {
                                                            backgroundColor: input.trim()
                                                                ? theme.palette.primary.dark
                                                                : theme.palette.action.disabled,
                                                        },
                                                        '&:disabled': {
                                                            color: theme.palette.text.disabled,
                                                        },
                                                    }}
                                                >
                                                    <SendIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default MvpAgentChatDialog;
